import type ApolloCache from '@wora/apollo-cache'
import { getOperationName } from 'apollo-link'
import type { DocumentNode } from 'graphql'
import type { CacheProxy } from '.'

const RE_QUERY_VARIABLES = /(\{.*\})/g

/**
 * Helper function for updating all queries in the cache with any variables. The update function
 * will run for each found query and write it with cache.writeQuery
 **/
export function updateQueries<T>(
  proxy: CacheProxy,
  {
    query,
    update
  }: {
    query: DocumentNode
    update: (data: T, variables?: Record<string, any>) => T
  }
) {
  const cache = proxy as ApolloCache

  // get all queries from cache, pull out the ones that match
  // this will look something like ["$ROOT_QUERY.searchQuotes({\"limit\": 10})"]
  const queryKeys = cache.cache.getAllKeys().filter((key) => key.startsWith(`$ROOT_QUERY.${getOperationName(query)}`))

  // process every matching query
  queryKeys.forEach((key) => {
    // pull out the variables from the query key
    const [vars] = key.match(RE_QUERY_VARIABLES) ?? []

    if (vars) {
      const variables = JSON.parse(vars)
      const data = cache.readQuery<T>({ query, variables })
      cache.writeQuery<T>({
        query,
        variables,
        data: update(data, variables)
      })
    }
  })
}

/**
 * Removes every cached variant of the given root query from the Apollo cache.
 * After eviction, a cache-first read for that query will miss and trigger a network fetch,
 * which lets `refetchQueries` fully refresh list views that weren't mounted when a mutation fired.
 *
 * NOTE: this reaches into the `@wora/apollo-cache` + `apollo-cache-inmemory` internal store
 * (`cache.cache.getAllKeys` / `.delete`) — the same internal contract already relied on by
 * `updateQueries` above. That store is set up in `apps/shared/graphql/client.ts`; if either
 * dep is upgraded, revisit both helpers together.
 */
export function evictQueries(proxy: CacheProxy, query: DocumentNode) {
  const cache = proxy as ApolloCache
  const opName = getOperationName(query)
  if (!opName) {
    // Only happens if a malformed DocumentNode is passed (no OperationDefinition).
    // eslint-disable-next-line no-console
    console.warn('evictQueries: document has no operation name; nothing to evict.')
    return
  }

  cache.cache
    .getAllKeys()
    .filter((key) => key.startsWith(`$ROOT_QUERY.${opName}`))
    .forEach((key) => cache.cache.delete(key))
}
